import React from 'react';
import ReactSelect, { SingleValue, ActionMeta, StylesConfig } from 'react-select';
import { useTheme } from '../../theme';

interface SelectOption {
  value: string;
  label: string;
}

interface EnhancedSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  label?: string;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  isSearchable?: boolean;
  isClearable?: boolean;
  menuPlacement?: 'auto' | 'bottom' | 'top';
}

const EnhancedSelect: React.FC<EnhancedSelectProps> = ({
  value,
  onChange,
  options,
  label,
  disabled = false,
  className = '',
  placeholder = 'Select...',
  isSearchable = true,
  isClearable = false,
  menuPlacement = 'auto',
}) => {
  // Find the selected option object
  const selectedOption = options.find(option => option.value === value) || null;

  const handleChange = (
    newValue: SingleValue<SelectOption>,
    actionMeta: ActionMeta<SelectOption>
  ) => {
    onChange(newValue ? newValue.value : '');
  };

  // Custom styles to match the existing design system
  const customStyles: StylesConfig<SelectOption, false> = {
    control: (provided, state) => ({
      ...provided,
      background: 'var(--semantic-colors-background-input)',
      border: state.isFocused
        ? 'var(--semantic-spacing-component-2xs) solid var(--semantic-colors-interactive-primary)'
        : 'var(--semantic-spacing-component-2xs) solid var(--semantic-colors-border-default)',
      borderRadius: 'var(--primitive-borders-radius-sm)',
      padding: 'var(--semantic-spacing-component-xs) var(--semantic-spacing-component-sm)',
      minHeight: 'var(--semantic-spacing-interactive-touchTarget)',
      boxShadow: 'none',
      fontSize: 'var(--primitive-typography-font-sizes-base)',
      color: 'var(--semantic-colors-text-primary)',
      '&:hover': {
        borderColor: 'var(--semantic-colors-interactive-primary)',
      },
    }),
    menu: (provided) => ({
      ...provided,
      background: 'var(--semantic-colors-background-primary)',
      border: 'var(--semantic-spacing-component-2xs) solid var(--semantic-colors-border-default)',
      borderRadius: 'var(--primitive-borders-radius-sm)',
      boxShadow: 'var(--semantic-shadows-medium)',
      zIndex: 9999,
      // Ensure solid background
      opacity: 1,
      backdropFilter: 'none',
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
    menuList: (provided) => ({
      ...provided,
      padding: 'var(--semantic-spacing-component-sm) 0',
      maxHeight: 'var(--semantic-spacing-viewport-listHeight)',
      background: 'var(--semantic-colors-background-primary)',
    }),
    option: (provided, state) => ({
      ...provided,
      background: state.isSelected
        ? 'var(--semantic-colors-interactive-primary)'
        : state.isFocused
          ? 'var(--semantic-colors-background-hover)'
          : 'var(--semantic-colors-background-card)',
      color: state.isSelected
        ? 'var(--semantic-colors-text-inverse)'
        : 'var(--semantic-colors-text-primary)',
      padding: 'var(--semantic-spacing-component-sm) var(--semantic-spacing-component-md)',
      cursor: 'pointer',
      fontSize: 'var(--primitive-typography-font-sizes-base)',
      '&:hover': {
        background: state.isSelected
          ? 'var(--semantic-colors-interactive-primary)'
          : 'var(--semantic-colors-background-hover)',
      },
    }),
    placeholder: (provided) => ({
      ...provided,
      color: 'var(--semantic-colors-text-secondary)',
      fontSize: 'var(--primitive-typography-font-sizes-base)',
    }),
    singleValue: (provided) => ({
      ...provided,
      color: 'var(--semantic-colors-text-primary)',
      fontSize: 'var(--primitive-typography-font-sizes-base)',
    }),
    input: (provided) => ({
      ...provided,
      color: 'var(--semantic-colors-text-primary)',
      fontSize: 'var(--primitive-typography-font-sizes-base)',
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      color: state.isFocused ? 'var(--semantic-colors-interactive-primary)' : 'var(--semantic-colors-text-secondary)',
      '&:hover': {
        color: 'var(--semantic-colors-interactive-primary)',
      },
    }),
  };

  return (
    <div className={`input-group ${className}`}>
      {label && (
        <label className="input-label">
          {label}
        </label>
      )}
      <ReactSelect<SelectOption>
        value={selectedOption}
        onChange={handleChange}
        options={options}
        isDisabled={disabled}
        placeholder={placeholder}
        isSearchable={isSearchable}
        isClearable={isClearable}
        menuPlacement={menuPlacement}
        menuPortalTarget={document.body}
        styles={customStyles}
        classNamePrefix="react-select"
        aria-label={label}
        // Performance optimizations
        filterOption={(option, inputValue) => {
          if (!inputValue) return true;
          const searchTerm = inputValue.toLowerCase();
          return (
            option.label.toLowerCase().includes(searchTerm) ||
            option.value.toLowerCase().includes(searchTerm)
          );
        }}
        // Accessibility improvements
        aria-describedby={label ? `${label.replace(/\s+/g, '-').toLowerCase()}-help` : undefined}
        // Mobile optimizations
        closeMenuOnScroll={true}
        captureMenuScroll={true}
      />
    </div>
  );
};

export default EnhancedSelect;